import { motion, AnimatePresence } from 'framer-motion'
import { useState } from 'react'
import { ArrowLeft, CreditCard, Lock, CheckCircle, Shield } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import { GlassmorphicCard } from '@/components/ui/glassmorphic-card'
// import { toast } from 'sonner'

export default function CheckoutPage() {
    const navigate = useNavigate()
    const [isLoading, setIsLoading] = useState(false)
    const [showSuccessModal, setShowSuccessModal] = useState(false)
    const [paymentMethod, setPaymentMethod] = useState('card')

    // Mock order details - in a real app this would come from state/context/query params
    const orderDetails = {
        item: 'Complete NEET 2025 Preparation',
        price: 19999,
        tax: 3600,
        total: 23599
    }

    const handlePayment = (e: React.FormEvent) => {
        e.preventDefault()
        setIsLoading(true)

        // Simulate payment processing
        setTimeout(() => {
            setIsLoading(false)
            setShowSuccessModal(true)
            // toast.success('Payment successful! Welcome aboard.')
            // navigate('/') // Redirect to dashboard after purchase
        }, 2000)
    }

    const handleCloseSuccess = () => {
        setShowSuccessModal(false)
        navigate('/dashboard')
    }

    return (
        <div className="min-h-screen pt-24 pb-12 px-4">
            <div className="container mx-auto max-w-6xl">
                <Link to="/marketplace" className="inline-flex items-center text-theme-secondary hover:text-primary-blue mb-8 transition-colors">
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back to Marketplace
                </Link>

                <div className="grid lg:grid-cols-3 gap-8">
                    {/* Checkout Form */}
                    <div className="lg:col-span-2 space-y-6">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                        >
                            <GlassmorphicCard className="p-8">
                                <h2 className="text-2xl font-display font-bold text-theme-primary mb-6 flex items-center gap-2">
                                    <Lock className="h-6 w-6 text-primary-blue" />
                                    Secure Checkout
                                </h2>

                                <form onSubmit={handlePayment} className="space-y-6">
                                    {/* Personal Info */}
                                    <div className="space-y-4">
                                        <h3 className="text-lg font-semibold text-theme-primary">Billing Details</h3>
                                        <div className="grid md:grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <label className="text-sm font-medium text-theme-secondary">First Name</label>
                                                <input required type="text" className="w-full px-4 py-3 bg-white/50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:border-primary-blue text-theme-primary" />
                                            </div>
                                            <div className="space-y-2">
                                                <label className="text-sm font-medium text-theme-secondary">Last Name</label>
                                                <input required type="text" className="w-full px-4 py-3 bg-white/50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:border-primary-blue text-theme-primary" />
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-sm font-medium text-theme-secondary">Email Address</label>
                                            <input required type="email" className="w-full px-4 py-3 bg-white/50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:border-primary-blue text-theme-primary" />
                                        </div>
                                    </div>

                                    <div className="border-t border-slate-200 dark:border-slate-700/50 my-6" />

                                    {/* Payment Method */}
                                    <div className="space-y-4">
                                        <h3 className="text-lg font-semibold text-theme-primary">Payment Method</h3>
                                        <div className="grid sm:grid-cols-3 gap-4">
                                            <button
                                                type="button"
                                                onClick={() => setPaymentMethod('card')}
                                                className={`p-4 rounded-xl border flex flex-col items-center gap-2 transition-all ${paymentMethod === 'card' ? 'border-primary-blue bg-primary-blue/10 text-primary-blue' : 'border-slate-200 dark:border-slate-700 bg-white/50 dark:bg-slate-800/50 text-theme-secondary hover:border-slate-300'}`}
                                            >
                                                <CreditCard className="h-6 w-6" />
                                                <span className="text-sm font-medium">Card</span>
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setPaymentMethod('upi')}
                                                className={`p-4 rounded-xl border flex flex-col items-center gap-2 transition-all ${paymentMethod === 'upi' ? 'border-primary-blue bg-primary-blue/10 text-primary-blue' : 'border-slate-200 dark:border-slate-700 bg-white/50 dark:bg-slate-800/50 text-theme-secondary hover:border-slate-300'}`}
                                            >
                                                <SmartphoneIcon className="h-6 w-6" />
                                                <span className="text-sm font-medium">UPI</span>
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setPaymentMethod('netbanking')}
                                                className={`p-4 rounded-xl border flex flex-col items-center gap-2 transition-all ${paymentMethod === 'netbanking' ? 'border-primary-blue bg-primary-blue/10 text-primary-blue' : 'border-slate-200 dark:border-slate-700 bg-white/50 dark:bg-slate-800/50 text-theme-secondary hover:border-slate-300'}`}
                                            >
                                                <BankIcon className="h-6 w-6" />
                                                <span className="text-sm font-medium">Net Banking</span>
                                            </button>
                                        </div>
                                    </div>

                                    {/* Payment Details (Mock) */}
                                    {paymentMethod === 'card' && (
                                        <div className="space-y-4 animate-in fade-in slide-in-from-top-2">
                                            <div className="space-y-2">
                                                <label className="text-sm font-medium text-theme-secondary">Card Number</label>
                                                <div className="relative">
                                                    <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                                                    <input required type="text" placeholder="0000 0000 0000 0000" className="w-full pl-10 pr-4 py-3 bg-white/50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:border-primary-blue text-theme-primary" />
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div className="space-y-2">
                                                    <label className="text-sm font-medium text-theme-secondary">Expiry</label>
                                                    <input required type="text" placeholder="MM/YY" className="w-full px-4 py-3 bg-white/50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:border-primary-blue text-theme-primary" />
                                                </div>
                                                <div className="space-y-2">
                                                    <label className="text-sm font-medium text-theme-secondary">CVC</label>
                                                    <input required type="text" placeholder="123" className="w-full px-4 py-3 bg-white/50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:border-primary-blue text-theme-primary" />
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <button
                                        type="submit"
                                        disabled={isLoading}
                                        className="w-full py-4 bg-primary-blue hover:bg-blue-600 text-white font-bold rounded-lg glow-primary transition-all duration-300 flex items-center justify-center gap-2 mt-8"
                                    >
                                        {isLoading ? (
                                            <span className="animate-spin w-5 h-5 border-2 border-white/20 border-t-white rounded-full" />
                                        ) : (
                                            <>
                                                Pay ₹{orderDetails.total.toLocaleString()}
                                                <Shield className="h-5 w-5" />
                                            </>
                                        )}
                                    </button>

                                    <p className="text-xs text-center text-slate-500 flex items-center justify-center gap-1">
                                        <Lock className="h-3 w-3" />
                                        Payments are secure and encrypted
                                    </p>
                                </form>
                            </GlassmorphicCard>
                        </motion.div>
                    </div>

                    {/* Order Summary */}
                    <div className="lg:col-span-1">
                        <motion.div
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.5, delay: 0.2 }}
                            className="sticky top-24"
                        >
                            <GlassmorphicCard className="p-6 bg-slate-50/50 dark:bg-slate-800/20">
                                <h3 className="text-xl font-display font-semibold text-theme-primary mb-4">Order Summary</h3>

                                <div className="flex gap-4 mb-6">
                                    <div className="w-20 h-20 rounded-lg bg-gradient-to-br from-primary-blue/20 to-accent-gold/20 flex items-center justify-center flex-shrink-0">
                                        <CheckCircle className="h-8 w-8 text-primary-blue" />
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-theme-primary line-clamp-2">{orderDetails.item}</h4>
                                        <p className="text-sm text-theme-muted mt-1">Lifetime Access</p>
                                    </div>
                                </div>

                                <div className="space-y-3 pt-4 border-t border-slate-200 dark:border-slate-700/50">
                                    <div className="flex justify-between text-theme-secondary">
                                        <span>Subtotal</span>
                                        <span>₹{orderDetails.price.toLocaleString()}</span>
                                    </div>
                                    <div className="flex justify-between text-theme-secondary">
                                        <span>Tax (18% GST)</span>
                                        <span>₹{orderDetails.tax.toLocaleString()}</span>
                                    </div>
                                    <div className="flex justify-between font-bold text-lg text-theme-primary pt-3 border-t border-slate-200 dark:border-slate-700/50">
                                        <span>Total</span>
                                        <span>₹{orderDetails.total.toLocaleString()}</span>
                                    </div>
                                </div>

                                <div className="mt-6 bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                                    <p className="text-sm text-blue-800 dark:text-blue-200">
                                        <strong>100% Money Back Guarantee:</strong> If you're not satisfied within 7 days, we'll refund your money. No questions asked.
                                    </p>
                                </div>
                            </GlassmorphicCard>
                        </motion.div>
                    </div>
                </div>
            </div>

            {/* Payment Success Modal */}
            <AnimatePresence>
                {showSuccessModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.9, y: 20 }}
                            className="w-full max-w-md"
                        >
                            <GlassmorphicCard className="p-8 text-center relative overflow-hidden">
                                {/* Success Icon Animation */}
                                <motion.div
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    transition={{ type: "spring", duration: 0.6, delay: 0.2 }}
                                    className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6"
                                >
                                    <CheckCircle className="h-10 w-10 text-green-500" />
                                </motion.div>

                                <h2 className="text-2xl font-display font-bold text-theme-primary mb-2">Payment Successful!</h2>
                                <p className="text-theme-secondary mb-8">
                                    Thank you for your purchase. You now have full access to your course.
                                </p>

                                <div className="space-y-3">
                                    <button
                                        onClick={handleCloseSuccess}
                                        className="w-full py-3 bg-primary-blue hover:bg-blue-600 text-white font-bold rounded-lg glow-primary transition-all duration-300"
                                    >
                                        Go to Dashboard
                                    </button>
                                </div>
                            </GlassmorphicCard>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    )
}

function SmartphoneIcon(props: React.SVGProps<SVGSVGElement>) {
    return (
        <svg
            {...props}
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <rect width="14" height="20" x="5" y="2" rx="2" ry="2" />
            <path d="M12 18h.01" />
        </svg>
    )
}

function BankIcon(props: React.SVGProps<SVGSVGElement>) {
    return (
        <svg
            {...props}
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <path d="M3 21h18" />
            <path d="M5 21v-7" />
            <path d="M19 21v-7" />
            <path d="M10 9L3 21" />
            <path d="M14 9l7 12" />
            <rect x="2" y="3" width="20" height="5" />
        </svg>
    )
}
